import { useState } from "react";
import { Link } from "react-router-dom";
import { Heart, ArrowLeftRight, ShoppingBag } from "lucide-react";
import { motion } from "framer-motion";
import StarRating from "./StarRating";
import { useShop } from "@/context/ShopContext";
import { formatPrice, SIZES } from "@/lib/constants";

const THIRTY_DAYS = 30 * 24 * 3600 * 1000;

const PerfumeCard = ({ p }) => {
  const sizes = p.sizes?.length ? p.sizes : SIZES;
  const [size, setSize] = useState(sizes.includes("50") ? "50" : sizes[0]);
  const { addToCart, favorites, toggleFavorite, compare, toggleCompare } = useShop();
  const fav = favorites.includes(p.id);
  const inCompare = compare.includes(p.id);
  const isPack = sizes.length === 3 && p.prices["30"] === p.prices["50"] && p.prices["50"] === p.prices["100"];
  const isNew = p.created_at && Date.now() - new Date(p.created_at).getTime() < THIRTY_DAYS;

  return (
    <motion.div
      data-testid="perfume-card-item"
      whileHover={{ y: -6 }}
      transition={{ duration: 0.3 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-[#EADFCB] bg-white shadow-sm transition-shadow hover:border-[#C59B27]/40 hover:shadow-xl hover:shadow-amber-950/10"
    >
      <Link to={`/parfum/${p.id}`} className="relative block aspect-[4/5] overflow-hidden bg-[#F7EEDB]">
        <div className="h-full w-full">
          {p.image ? (
            <img
              src={p.image}
              alt={p.name}
              loading="lazy"
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#F7EEDB] to-[#F2A7A0]/30 font-accent text-4xl text-[#C59B27]">
              {p.brand?.[0]}
            </div>
          )}
        </div>
        <div className="absolute left-3 top-3 flex flex-col gap-1.5">
          {!p.in_stock && (
            <span className="rounded-full bg-[#1A1714] px-3 py-1 text-xs font-semibold text-white">Rupture</span>
          )}
          {isNew && (
            <span data-testid={`new-badge-${p.id}`} className="rounded-full bg-[#C59B27] px-3 py-1 text-xs font-bold uppercase tracking-wider text-white shadow-md shadow-[#C59B27]/40">
              Nouveau
            </span>
          )}
        </div>
        <span className="absolute bottom-3 left-3 rounded-full bg-white/85 px-3 py-1 text-xs font-medium text-[#5C5449] backdrop-blur">
          {p.family}
        </span>
      </Link>

      <div className="absolute right-3 top-3 flex flex-col gap-2">
        <button
          data-testid={`favorite-toggle-${p.id}`}
          onClick={() => toggleFavorite(p.id)}
          aria-label="Ajouter aux favoris"
          className={`flex h-9 w-9 items-center justify-center rounded-full backdrop-blur transition-all active:scale-90 ${
            fav ? "bg-[#F2A7A0] text-white" : "bg-white/85 text-[#5C5449] hover:text-[#C59B27]"
          }`}
        >
          <Heart size={16} className={fav ? "fill-white" : ""} />
        </button>
        <button
          data-testid={`compare-toggle-${p.id}`}
          onClick={() => toggleCompare(p.id)}
          aria-label="Comparer"
          className={`flex h-9 w-9 items-center justify-center rounded-full backdrop-blur transition-all active:scale-90 ${
            inCompare ? "bg-[#C59B27] text-white" : "bg-white/85 text-[#5C5449] hover:text-[#C59B27]"
          }`}
        >
          <ArrowLeftRight size={16} />
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <Link
          to={`/marque/${encodeURIComponent(p.brand)}`}
          data-testid={`brand-link-${p.id}`}
          className="w-fit text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27] transition-colors hover:text-[#FF8A00]"
        >
          {p.brand}
        </Link>
        <Link
          to={`/parfum/${p.id}`}
          className="font-serif text-lg font-semibold leading-tight text-[#1A1714] transition-colors hover:text-[#C59B27]"
        >
          {p.name}
        </Link>
        <div className="flex items-center gap-2">
          <StarRating value={p.rating || 0} size={13} />
          <span className="text-xs text-[#5C5449]">({p.review_count || 0} avis)</span>
        </div>
        {isPack ? (
          <div className="mt-1">
            <span data-testid={`pack-badge-${p.id}`} className="inline-block rounded-full bg-[#F7EEDB] px-3 py-1.5 text-xs font-semibold text-[#C59B27]">
              Coffret duo — 2 × 100 ml
            </span>
          </div>
        ) : sizes.length === 1 ? (
          <div className="mt-1">
            <span data-testid={`size-single-${p.id}`} className="inline-block rounded-full bg-[#F7EEDB] px-3 py-1.5 text-xs font-semibold text-[#C59B27]">
              {sizes[0]} ml — format unique
            </span>
          </div>
        ) : (
          <div className="mt-1 flex gap-1.5">
            {sizes.map((s) => (
              <button
                key={s}
                data-testid={`size-${s}-${p.id}`}
                onClick={() => setSize(s)}
                className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                  size === s
                    ? "border-[#C59B27] bg-[#C59B27] text-white"
                    : "border-[#EADFCB] text-[#5C5449] hover:border-[#C59B27]/50"
                }`}
              >
                {s} ml
              </button>
            ))}
          </div>
        )}
        <div className="mt-auto flex items-center justify-between pt-2">
          <span className="font-serif text-xl font-bold text-[#1A1714]">{formatPrice(p.prices[size])}</span>
          <button
            data-testid={`add-to-cart-${p.id}`}
            disabled={!p.in_stock}
            onClick={() => addToCart(p, size)}
            className="flex items-center gap-1.5 rounded-full bg-[#1A1714] px-4 py-2 text-xs font-semibold text-white transition-all hover:bg-[#C59B27] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ShoppingBag size={14} /> Ajouter
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default PerfumeCard;
